/**
 * PageList - Left panel showing toggleable/draggable proposal pages
 * Similar to SumoQuote's page builder sidebar
 */

import React from 'react';
import {
  Box,
  Typography,
  Switch,
  IconButton,
  Card,
  Stack,
  Button,
} from '@mui/joy';
import {
  DotsSixVertical as DragIndicator,
  TextHOne as Title,
  FileText as Description,
  Image,
  Images as PhotoLibrary,
  Ruler as Straighten,
  Invoice as RequestQuote,
  Pencil as Draw,
  Gavel,
  Plus as Add,
} from '@phosphor-icons/react';
import type { ProposalPage, PageType } from '../../types/proposals';

interface PageListProps {
  pages: ProposalPage[];
  selectedPageId: number | null;
  onSelectPage: (pageId: number) => void;
  onTogglePage: (pageId: number, enabled: boolean) => void;
  onReorderPages: (pages: ProposalPage[]) => void;
  onAddCustomPage: () => void;
}

const pageIcons: Record<PageType, React.ReactNode> = {
  title: <Title />,
  introduction: <Description />,
  manufacturer_resource: <Image />,
  manufacturer_marketing: <Image />,
  manufacturer_colors: <Image />,
  manufacturer_other: <Image />,
  inspection: <PhotoLibrary />,
  measurements: <Straighten />,
  quote_details: <RequestQuote />,
  authorization: <Draw />,
  terms: <Gavel />,
  custom: <Description />,
};

const pageLabels: Record<PageType, string> = {
  title: 'Title',
  introduction: 'Introduction',
  manufacturer_resource: 'Product Page',
  manufacturer_marketing: 'Manufacturer Marketing',
  manufacturer_colors: 'Colors & Options',
  manufacturer_other: 'Other Materials',
  inspection: 'Inspection',
  measurements: 'Measurements',
  quote_details: 'Quote Details',
  authorization: 'Authorization',
  terms: 'Terms and Conditions',
  custom: 'Custom Page',
};

export const PageList: React.FC<PageListProps> = ({
  pages,
  selectedPageId,
  onSelectPage,
  onTogglePage,
  onReorderPages,
  onAddCustomPage,
}) => {
  const sortedPages = [...pages].sort((a, b) => a.sort_order - b.sort_order);

  const [draggingId, setDraggingId] = React.useState<number | null>(null);
  const [dragOverId, setDragOverId] = React.useState<number | null>(null);

  const handleDrop = (targetId: number) => {
    if (draggingId === null || draggingId === targetId) {
      setDraggingId(null);
      setDragOverId(null);
      return;
    }
    const fromIndex = sortedPages.findIndex((p) => p.id === draggingId);
    const toIndex = sortedPages.findIndex((p) => p.id === targetId);
    if (fromIndex === -1 || toIndex === -1) {
      setDraggingId(null);
      setDragOverId(null);
      return;
    }
    const reordered = [...sortedPages];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setDraggingId(null);
    setDragOverId(null);
    onReorderPages(reordered);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography level="title-lg">Pages</Typography>
        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
          Toggle pages on/off to include in proposal
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        <Stack spacing={0.5}>
          {sortedPages.map((page) => (
            <Card
              key={page.id}
              variant={selectedPageId === page.id ? 'soft' : 'plain'}
              color={selectedPageId === page.id ? 'primary' : 'neutral'}
              draggable
              onDragStart={() => setDraggingId(page.id)}
              onDragEnd={() => {
                setDraggingId(null);
                setDragOverId(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragOverId !== page.id) setDragOverId(page.id);
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(page.id);
              }}
              sx={{
                cursor: 'pointer',
                opacity: draggingId === page.id ? 0.4 : page.is_enabled ? 1 : 0.5,
                transition: 'all 0.15s',
                borderTop: dragOverId === page.id && draggingId !== page.id
                  ? '2px solid'
                  : '2px solid transparent',
                borderTopColor: dragOverId === page.id && draggingId !== page.id
                  ? 'primary.500'
                  : 'transparent',
                '&:hover': {
                  backgroundColor: selectedPageId === page.id
                    ? 'primary.softHoverBg'
                    : 'neutral.softHoverBg',
                },
              }}
              onClick={() => onSelectPage(page.id)}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                }}
              >
                <IconButton
                  size="sm"
                  variant="plain"
                  sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                >
                  <DragIndicator size={18} />
                </IconButton>

                <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                  {pageIcons[page.page_type]}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    level="body-sm"
                    fontWeight="md"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {page.title || pageLabels[page.page_type]}
                  </Typography>
                  {page.page_type === 'manufacturer_resource' && page.manufacturer_resource_title && (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {page.manufacturer_resource_title}
                    </Typography>
                  )}
                </Box>

                <Switch
                  size="sm"
                  checked={page.is_enabled}
                  onChange={(e) => {
                    e.stopPropagation();
                    onTogglePage(page.id, e.target.checked);
                  }}
                />
              </Box>
            </Card>
          ))}
        </Stack>
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          startDecorator={<Add />}
          onClick={onAddCustomPage}
        >
          Add Custom Page
        </Button>
      </Box>
    </Box>
  );
};
